import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode, RefObject } from 'react';

const SHEET_BREAKPOINT = 640;
const MENU_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

interface Position {
  top: number;
  left: number;
  placement: 'above' | 'below';
}

/**
 * A menu anchored to a control.
 *
 * ⭐ Rendered in a portal with fixed positioning rather than absolutely inside the row: the backlog
 * is a scrolling list, and an absolutely-positioned menu inside it gets clipped by the first
 * ancestor with `overflow`. That failure is invisible on desktop and total on a phone.
 *
 * 🔴 On a narrow screen it is a BOTTOM SHEET, not a tiny floating menu. A 25-status list rendered as
 * a 260px dropdown next to a 44px row is technically the same component and practically unusable —
 * the same re-think, not reflow, rule as the board.
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  label,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  label: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isSheet, setIsSheet] = useState(() => window.innerWidth < SHEET_BREAKPOINT);
  const [position, setPosition] = useState<Position | null>(null);

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const sheet = window.innerWidth < SHEET_BREAKPOINT;
      setIsSheet(sheet);
      if (sheet) return;

      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const estimatedHeight = panelRef.current?.offsetHeight ?? 280;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement: Position['placement'] =
        spaceBelow < estimatedHeight + VIEWPORT_MARGIN && rect.top > estimatedHeight ? 'above' : 'below';

      const left = Math.min(
        Math.max(VIEWPORT_MARGIN, rect.left),
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN,
      );
      const top = placement === 'below' ? rect.bottom + 4 : Math.max(VIEWPORT_MARGIN, rect.top - estimatedHeight - 4);
      setPosition({ top, left, placement });
    };

    place();
    window.addEventListener('resize', place);
    // `true` — capture, so a scroll in ANY ancestor repositions the menu, not just the window.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        anchorRef.current?.focus();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  if (isSheet) {
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-end" role="presentation">
        <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="safe-bottom relative max-h-[75vh] w-full overflow-y-auto rounded-t-xl border-t border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] shadow-2xl"
        >
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] px-4 py-3">
            <p className="text-sm font-semibold">{label}</p>
            <button
              type="button"
              onClick={onClose}
              className="rounded px-2 py-1 text-sm text-[color:var(--color-ink-muted)] hover:bg-black/5 dark:hover:bg-white/10"
            >
              Close
            </button>
          </div>
          {children}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={label}
      style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width: MENU_WIDTH }}
      className="fixed z-50 max-h-[60vh] overflow-y-auto rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface-raised)] py-1 shadow-xl"
    >
      {children}
    </div>,
    document.body,
  );
}
